/**
 * Client File Management API Service
 * Handles API calls for clients and folders
 */

import apiClient from './client';
import type {
  Client,
  Folder,
  FolderContents,
  ClientStats,
  FolderStats,
  ClientListResponse,
  ClientSearchResult,
  CreateClientRequest,
  UpdateClientRequest,
  CreateFolderRequest,
  UpdateFolderRequest,
  ClientListFilters,
  FolderListFilters,
  MetadataProcessingStats,
  BatchProcessingResult,
  FileMetadataResponse,
  FailedFile,
} from '../../types/client';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
  message?: string;
}

export interface FirmFileTimelineItem {
  id: string;
  /** 'client' = uploaded by the client; 'firm' = generated/shared by the firm. */
  side: 'firm' | 'client';
  title: string;
  filename: string;
  contentType?: string | null;
  senderName?: string | null;
  occurredAt: string;
}

class ClientsService {
  // ============= Client Operations =============

  /**
   * List clients with optional filters
   */
  async listClients(filters: ClientListFilters = {}): Promise<ClientListResponse> {
    const queryParams = new URLSearchParams();

    if (filters.status) {
      queryParams.append('status', Array.isArray(filters.status) ? filters.status.join(',') : filters.status);
    }
    if (filters.clientType) {
      queryParams.append('clientType', Array.isArray(filters.clientType) ? filters.clientType.join(',') : filters.clientType);
    }
    if (filters.search) queryParams.append('search', filters.search);
    if (filters.industry) queryParams.append('industry', filters.industry);
    if (filters.tags?.length) queryParams.append('tags', filters.tags.join(','));
    if (filters.sortBy) queryParams.append('sortBy', filters.sortBy);
    if (filters.sortOrder) queryParams.append('sortOrder', filters.sortOrder);
    if (filters.page) queryParams.append('page', filters.page.toString());
    if (filters.limit) queryParams.append('limit', filters.limit.toString());

    const response = await apiClient.get<ApiResponse<ClientListResponse>>(`/clients?${queryParams.toString()}`);
    return response.data.data;
  }

  /**
   * Get a single client by ID
   */
  async getClient(clientId: string): Promise<Client> {
    const response = await apiClient.get<ApiResponse<Client>>(`/clients/${clientId}`);
    return response.data.data;
  }

  /**
   * Create a new client
   */
  async createClient(data: CreateClientRequest): Promise<Client> {
    const response = await apiClient.post<ApiResponse<Client>>('/clients', data);
    return response.data.data;
  }

  /**
   * Update an existing client
   */
  async updateClient(clientId: string, data: UpdateClientRequest): Promise<Client> {
    const response = await apiClient.put<ApiResponse<Client>>(`/clients/${clientId}`, data);
    return response.data.data;
  }

  /**
   * Delete a client
   */
  async deleteClient(clientId: string): Promise<void> {
    await apiClient.delete(`/clients/${clientId}`);
  }

  /**
   * Search clients for autocomplete
   */
  async searchClients(query: string, limit: number = 10): Promise<ClientSearchResult[]> {
    const queryParams = new URLSearchParams();
    queryParams.append('q', query);
    queryParams.append('limit', limit.toString());

    const response = await apiClient.get<ApiResponse<ClientSearchResult[]>>(`/clients/search?${queryParams.toString()}`);
    return response.data.data;
  }

  /**
   * Get client statistics
   */
  async getClientStats(): Promise<ClientStats> {
    const response = await apiClient.get<ApiResponse<ClientStats>>('/clients/stats');
    return response.data.data;
  }

  /**
   * Get client deadlines (not yet implemented on backend)
   */
  async getClientDeadlines(_clientId: string): Promise<any[]> {
    return [];
  }

  /**
   * Get client's root folder and folder structure
   */
  async getClientFolders(clientId: string): Promise<FolderContents> {
    const response = await apiClient.get<ApiResponse<FolderContents>>(`/clients/${clientId}/folders`);
    return response.data.data;
  }

  // ============= Folder Operations =============

  /**
   * List folders with optional filters
   */
  async listFolders(filters: FolderListFilters = {}): Promise<Folder[]> {
    const queryParams = new URLSearchParams();

    if (filters.clientId) queryParams.append('clientId', filters.clientId);
    if (filters.parentFolderId !== undefined) {
      queryParams.append('parentFolderId', filters.parentFolderId === null ? 'null' : filters.parentFolderId);
    }
    if (filters.folderType) queryParams.append('folderType', filters.folderType);
    if (filters.search) queryParams.append('search', filters.search);
    if (filters.sortBy) queryParams.append('sortBy', filters.sortBy);
    if (filters.sortOrder) queryParams.append('sortOrder', filters.sortOrder);

    const response = await apiClient.get<ApiResponse<Folder[]>>(`/folders?${queryParams.toString()}`);
    return response.data.data;
  }

  /**
   * Get a single folder by ID
   */
  async getFolder(folderId: string): Promise<Folder> {
    const response = await apiClient.get<ApiResponse<Folder>>(`/folders/${folderId}`);
    return response.data.data;
  }

  /**
   * Get folder contents (subfolders + documents + breadcrumbs)
   */
  async getFolderContents(folderId: string): Promise<FolderContents> {
    const response = await apiClient.get<ApiResponse<FolderContents>>(`/folders/${folderId}/contents`);
    return response.data.data;
  }

  /**
   * Create a new folder
   */
  async createFolder(data: CreateFolderRequest): Promise<Folder> {
    const response = await apiClient.post<ApiResponse<Folder>>('/folders', data);
    return response.data.data;
  }

  /**
   * Update an existing folder
   */
  async updateFolder(folderId: string, data: UpdateFolderRequest): Promise<Folder> {
    const response = await apiClient.put<ApiResponse<Folder>>(`/folders/${folderId}`, data);
    return response.data.data;
  }

  /**
   * Delete a folder (and all subfolders/documents)
   */
  async deleteFolder(folderId: string): Promise<void> {
    await apiClient.delete(`/folders/${folderId}`);
  }

  /**
   * Move a folder to a new parent
   */
  async moveFolder(folderId: string, newParentId: string | null): Promise<Folder> {
    const response = await apiClient.put<ApiResponse<Folder>>(`/folders/${folderId}/move`, { newParentId });
    return response.data.data;
  }

  /**
   * Get folder statistics
   */
  async getFolderStats(clientId?: string): Promise<FolderStats> {
    const queryParams = new URLSearchParams();
    if (clientId) queryParams.append('clientId', clientId);

    const response = await apiClient.get<ApiResponse<FolderStats>>(`/folders/stats?${queryParams.toString()}`);
    return response.data.data;
  }

  // ============= Metadata Extraction =============

  /**
   * Get metadata extraction processing stats for a client
   */
  async getMetadataStats(clientId: string): Promise<MetadataProcessingStats> {
    const response = await apiClient.get<ApiResponse<MetadataProcessingStats>>(
      `/synced-files/extract-metadata?clientId=${clientId}`
    );
    return response.data.data;
  }

  /**
   * Reset all completed/failed files to pending for re-indexing
   */
  async reindexFiles(clientId: string): Promise<{ reset: number }> {
    const response = await apiClient.put<ApiResponse<{ reset: number }>>(
      '/synced-files/extract-metadata',
      { clientId }
    );
    return response.data.data;
  }

  /**
   * Trigger metadata extraction batch processing for a client
   */
  async extractMetadata(clientId: string, batchSize: number = 20): Promise<BatchProcessingResult> {
    const response = await apiClient.post<ApiResponse<BatchProcessingResult>>(
      '/synced-files/extract-metadata',
      { clientId, batchSize }
    );
    return response.data.data;
  }

  /**
   * Get list of failed files with error reasons
   */
  async getFailedFiles(clientId: string): Promise<FailedFile[]> {
    const response = await apiClient.get<ApiResponse<FailedFile[]>>(
      `/synced-files/failed?clientId=${clientId}`
    );
    return response.data.data;
  }

  /**
   * Reset a single failed file and optionally re-trigger analysis
   */
  async retryFailedFile(fileId: string, clientId: string): Promise<{ reset: boolean; reprocessing: boolean }> {
    const response = await apiClient.post<ApiResponse<{ reset: boolean; reprocessing: boolean }>>(
      '/synced-files/failed',
      { fileId, clientId, reprocess: true }
    );
    return response.data.data;
  }

  // ============= File Operations =============

  /**
   * Get full metadata and embedding chunks for a synced file
   */
  async getFileMetadata(fileId: string): Promise<FileMetadataResponse> {
    const response = await apiClient.get<ApiResponse<FileMetadataResponse>>(`/synced-files/${fileId}/metadata`);
    return response.data.data;
  }

  /**
   * Get presigned download URL for a synced file
   */
  async getFileDownloadUrl(fileId: string): Promise<{ downloadUrl: string; fileName: string; mimeType: string; fileSize: number }> {
    const response = await apiClient.get<ApiResponse<{ downloadUrl: string; fileName: string; mimeType: string; fileSize: number }>>(`/synced-files/${fileId}/download`);
    return response.data.data;
  }

  /**
   * Two-sided file history for a client (Firm vs Client sides).
   */
  async getClientFilesTimeline(clientId: string): Promise<FirmFileTimelineItem[]> {
    const response = await apiClient.get<ApiResponse<{ items: FirmFileTimelineItem[] }>>(`/clients/${clientId}/files-timeline`);
    return response.data.data?.items || [];
  }

  // ============= Client File Chat =============

  /**
   * Chat with a client's indexed files
   */
  async chatWithClientFiles(request: { clientId: string; message: string; conversationHistory?: any[]; dataSource?: string }): Promise<any> {
    const response = await apiClient.post<ApiResponse<any>>(
      `/clients/${request.clientId}/chat`,
      request
    );
    return response.data.data;
  }

  // ==================== Chat Conversations ====================

  async listConversations(clientId: string) {
    const response = await apiClient.get<ApiResponse<any[]>>(`/clients/${clientId}/conversations`);
    return response.data.data;
  }

  async getConversation(clientId: string, conversationId: string) {
    const response = await apiClient.get<ApiResponse<any>>(`/clients/${clientId}/conversations/${conversationId}`);
    return response.data.data;
  }

  async saveConversation(clientId: string, data: { id?: string; title?: string; messages: any[]; dataSource?: string }) {
    const response = await apiClient.post<ApiResponse<any>>(`/clients/${clientId}/conversations`, data);
    return response.data.data;
  }

  async deleteConversation(clientId: string, conversationId: string) {
    const response = await apiClient.delete<ApiResponse<any>>(`/clients/${clientId}/conversations/${conversationId}`);
    return response.data.data;
  }
}

export const clientsService = new ClientsService();
export default clientsService;
